"use client"

import { useEffect, useState, ReactNode } from "react"
import { useAuth } from "@/components/auth-provider"
import {
  requestUserScopes,
  parseUserInfo,
  mpesaLogin,
  isHylidBridgeAvailable,
  SSOErrorResponse,
  SSOSuccessResponse,
  UserScope,
} from "@/lib/sso"

/**
 * SSO Handler Component
 * 
 * Automatically handles Single Sign-On (SSO) authentication when the app
 * is accessed from https://mpesa-test.qetero.com/
 * 
 * This component:
 * 1. Checks if the app is running in M-Pesa environment (mpesa-test.qetero.com)
 * 2. Requests user scopes via hylid-bridge
 * 3. Authenticates the user with the backend
 * 4. Integrates with AuthProvider to log the user in
 */
export function SSOHandler({ children }: { children: ReactNode }) {
  const { login, isAuthenticated, isLoading: authLoading } = useAuth()
  const [ssoLoading, setSsoLoading] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [ssoAttempted, setSsoAttempted] = useState(false)

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    // Check if we should attempt SSO
    const shouldAttemptSSO = () => {
      const origin = window.location.origin
      const isMpesaOrigin = origin === 'https://mpesa-test.qetero.com'
      return isMpesaOrigin && !isAuthenticated && !authLoading && !ssoAttempted
    }

    // Only attempt SSO if conditions are met
    if (!shouldAttemptSSO()) {
      return
    }

    const performSSO = async () => {
      setSsoAttempted(true)
      setSsoLoading(true)
      setSsoError(null)

      try {
        // Check if hylid-bridge is available
        if (!isHylidBridgeAvailable()) {
          console.warn('[SSO Handler] ⚠️ hylid-bridge not available, skipping SSO')
          setSsoLoading(false)
          return
        }

        // Define the scopes to request
        const requestedScopes: UserScope[] = [
          "USER_NAME",
          "USER_LNAME",
          "USER_MNAME",
          "USER_DOB",
          "USER_ID",
          "USER_IDNO",
          "USER_MOBILE",
        ]

        // Request user scopes from M-Pesa App
        const scopeResponse = await requestUserScopes(requestedScopes)

        // Check if response is an error
        if ('error' in scopeResponse) {
          const errorResponse = scopeResponse as SSOErrorResponse
          const errorMessage = errorResponse.errorMessage || 'Operation failed'
          
          console.error('[SSO Handler] ❌ SSO Error:', errorResponse)
          setSsoError(errorMessage)

          // Show error alert using hylid-bridge
          try {
            const hylidBridge = await import('hylid-bridge')
            const bridgeAlert = hylidBridge.alert || (window as any).alert
            if (typeof bridgeAlert === 'function') {
              bridgeAlert({
                content: `SSO Error: ${errorMessage} (Code: ${errorResponse.error})`,
              })
            }
          } catch (alertErr) {
            console.error('[SSO Handler] Failed to show alert:', alertErr)
          }

          setSsoLoading(false)
          return
        }

        // Parse the successful response
        const successResponse = scopeResponse as SSOSuccessResponse

        // Log rejected scopes if any
        if (successResponse.kycRejected && successResponse.kycRejected.length > 0) {
          console.warn('[SSO Handler] ⚠️ Some scopes were rejected:', successResponse.kycRejected)
        }

        // Parse user info from the response
        const userInfo = parseUserInfo(successResponse)

        // Validate that we have at least mobile number (required for authentication)
        if (!userInfo.mobile) {
          throw new Error('Mobile number is required but was not provided')
        }

        // Send user info to backend for authentication
        const loginResponse = await mpesaLogin(userInfo)

        if (loginResponse.token) {
          login(loginResponse.token)
        } else {
          throw new Error('No token received from backend')
        }
      } catch (error) {
        console.error('[SSO Handler] ❌ SSO authentication failed:', error)
        
        const errorMessage = error instanceof Error ? error.message : 'SSO authentication failed'
        setSsoError(errorMessage)

        // Show error alert
        try {
          const hylidBridge = await import('hylid-bridge')
          const bridgeAlert = hylidBridge.alert || (window as any).alert
          if (typeof bridgeAlert === 'function') {
            bridgeAlert({
              content: `SSO Error: ${errorMessage}`,
            })
          }
        } catch (alertErr) {
          console.error('[SSO Handler] Failed to show alert:', alertErr)
        }
      } finally {
        setSsoLoading(false)
      }
    }

    // Small delay to ensure auth provider has finished loading
    const timeoutId = setTimeout(() => {
      performSSO()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isAuthenticated, authLoading, login, ssoAttempted])

  // Render children normally - SSO happens in the background
  return <>{children}</>
}
